import React, { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  type ColumnDef,
  type ExpandedState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type Row,
} from '@tanstack/react-table';

import PaginationWrapper from 'components/partials/table-component/pagination-wrapper/pagination-wrapper';
import { Table } from 'components/partials/table-component/table/table';

import classNames from 'classnames';

export type TableLooseColumnDef = {
  id?: string;
  accessorKey?: string;
  header?: unknown;
  cell?: unknown;
  sortingFn?: unknown;
  headerClassName?: string;
  className?: string;
  isHeaderCentered?: boolean;
  enableSorting?: boolean;
};

export type TableColumnDef<TData = unknown> =
  | ColumnDef<TData, unknown>
  | TableLooseColumnDef;

export interface TableAmountState {
  pageSize: number;
  totalRecords: number;
}

export interface TableComponentProps<TData = unknown> {
  columns: TableColumnDef<TData>[];
  data: TData[];
  isLoading: boolean;
  /** Required when `pagination !== false`; otherwise defaults to `0`. */
  pageIndex?: number;
  /** Required when `pagination !== false`; otherwise defaults to `data.length || 10`. */
  pageSize?: number;
  setPagination?: (pageIndex: number) => void;
  setAmount?: (updater: (state: TableAmountState) => TableAmountState) => void;
  /** Defaults to `[]` (no sorting) when omitted. */
  sortingParams?: SortingState;
  manualSorting?: boolean;
  /** Defaults to a no-op when omitted. */
  setSortingParams?: OnChangeFn<SortingState>;
  isMultiSort?: boolean;
  /** Required when `pagination !== false`; otherwise defaults to `data.length`. */
  totalRecords?: number;
  showingRecordsTextIsOn?: boolean;
  viewByAmountSelectIsOn?: boolean;
  pagination?: boolean;
  customHeight?: string;
  expandable?: boolean;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getSubRows?: (row: TData) => TData[] | undefined;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactNode;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  paginateExpandedRows?: boolean;
  manualPagination?: boolean;
}

const NOOP_SORTING_CHANGE: OnChangeFn<SortingState> = () => undefined;

const TableComponentImpl = <TData,>({
  columns,
  data,
  isLoading,
  pageIndex = 0,
  pageSize,
  setPagination,
  setAmount,
  sortingParams = [],
  manualSorting = true,
  setSortingParams = NOOP_SORTING_CHANGE,
  isMultiSort = false,
  totalRecords,
  showingRecordsTextIsOn = true,
  viewByAmountSelectIsOn = true,
  pagination = true,
  customHeight = 'max-h-[62vh]',
  expandable = false,
  expanded: controlledExpanded,
  onExpandedChange: controlledOnExpandedChange,
  getSubRows,
  renderSubComponent,
  getRowCanExpand,
  paginateExpandedRows = true,
  manualPagination = true,
}: TableComponentProps<TData>): React.JSX.Element => {
  const effectivePageSize = pageSize ?? (data.length || 10);
  const effectiveTotalRecords = totalRecords ?? data.length;
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

  const expanded = controlledExpanded ?? internalExpanded;
  const onExpandedChange = controlledOnExpandedChange ?? setInternalExpanded;

  const pagesAmount = useMemo(
    () => Math.ceil(effectiveTotalRecords / effectivePageSize),
    [effectivePageSize, effectiveTotalRecords]
  );

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next =
      typeof updater === 'function'
        ? updater({ pageIndex, pageSize: effectivePageSize })
        : updater;

    setPagination?.(next.pageIndex);
  };

  const handleGetRowCanExpand = useCallback(
    (row: Row<TData>): boolean => {
      if (getRowCanExpand) {
        return getRowCanExpand(row);
      }

      if (renderSubComponent) {
        return true;
      }

      return row.subRows?.length > 0;
    },
    [getRowCanExpand, renderSubComponent]
  );

  const table = useReactTable<TData>({
    data,
    columns: columns as ColumnDef<TData, unknown>[],
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: pagesAmount,
    state: {
      sorting: sortingParams,
      pagination: { pageIndex, pageSize: effectivePageSize },
      ...(expandable && { expanded }),
    },
    onPaginationChange: onPaginationChange,
    enableSortingRemoval: false,
    onSortingChange: setSortingParams,
    enableMultiSort: isMultiSort,
    isMultiSortEvent: () => true,
    maxMultiSortColCount: 2,
    getSortedRowModel: getSortedRowModel(),
    manualPagination,
    debugTable: true,
    ...(expandable && {
      onExpandedChange,
      getExpandedRowModel: getExpandedRowModel(),
      getSubRows,
      getRowCanExpand: handleGetRowCanExpand,
      paginateExpandedRows,
    }),
  });

  return (
    <div className="w-full flex-column" data-testid="table-container">
      {!!pagination && (
        <div className={classNames('flex items-center mb-2 justify-end')}>
          <PaginationWrapper
            showingRecordsTextIsOn={showingRecordsTextIsOn}
            viewByAmountSelectIsOn={viewByAmountSelectIsOn}
            table={table as unknown as Parameters<typeof PaginationWrapper>[0]['table']}
            pageIndex={pageIndex}
            pageSize={effectivePageSize}
            totalRecords={effectiveTotalRecords}
            pagesAmount={pagesAmount}
            setAmount={setAmount}
          />
        </div>
      )}
      <Table
        {...{
          table: table as unknown as Parameters<typeof Table>[0]['table'],
          sortingParams,
          isLoading,
          isMultiSort,
          customHeight,
          expandable,
          renderSubComponent: renderSubComponent as unknown as
            | ((props: { row: Row<unknown> }) => React.ReactNode)
            | undefined,
        }}
      />
    </div>
  );
};

const TableComponent = TableComponentImpl as unknown as (<TData = unknown>(
  props: TableComponentProps<TData>
) => React.ReactElement);

export { TableComponent };
export default TableComponent;
